import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import jayzLogo from "@/assets/jayz_logo.svg";

type Status = "loading" | "valid" | "already" | "invalid" | "success" | "error";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${token}`,
          { headers: { apikey: ANON_KEY } }
        );
        const data = await res.json();
        if (!res.ok) {
          setStatus("invalid");
        } else if (data.valid === false && data.reason === "already_unsubscribed") {
          setStatus("already");
        } else if (data.valid) {
          setStatus("valid");
        } else {
          setStatus("invalid");
        }
      } catch {
        setStatus("invalid");
      }
    })();
  }, [token]);

  const handleUnsubscribe = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
        body: { token },
      });
      if (error) throw error;
      if (data?.success) {
        setStatus("success");
      } else if (data?.reason === "already_unsubscribed") {
        setStatus("already");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-10 text-center shadow-sm">
        <img src={jayzLogo} alt="JAYZ Services" className="mx-auto mb-8 h-7" />

        {status === "loading" && (
          <p className="text-sm text-muted-foreground">Validating your request…</p>
        )}

        {status === "valid" && (
          <>
            <h1 className="text-xl font-semibold text-foreground">Unsubscribe</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Are you sure you want to unsubscribe from future emails?
            </p>
            <button
              onClick={handleUnsubscribe}
              className="mt-6 inline-flex h-11 items-center justify-center rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/85 active:scale-[0.97]"
            >
              Confirm Unsubscribe
            </button>
          </>
        )}

        {status === "success" && (
          <>
            <h1 className="text-xl font-semibold text-foreground">You've been unsubscribed</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              You will no longer receive emails from us.
            </p>
          </>
        )}

        {status === "already" && (
          <>
            <h1 className="text-xl font-semibold text-foreground">Already unsubscribed</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              This email address has already been unsubscribed.
            </p>
          </>
        )}

        {status === "invalid" && (
          <>
            <h1 className="text-xl font-semibold text-foreground">Invalid link</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              This unsubscribe link is invalid or has expired.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              We couldn't process your request. Please try again later.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
